/** Authenticated application shell with one floating navigation state owner. */
import { Outlet } from 'react-router-dom'
import { useEffect, useId, useLayoutEffect, useRef, memo } from 'react'
import { useIsAuthenticated } from '@azure/msal-react'
import { IslandSidebar } from '@/components/layout/IslandSidebar'
import DocumentTitle from '@/components/common/DocumentTitle'
import RequireAuth from '@/components/RequireAuth'
import { websocketManager } from '@/services/websocketCompat'
import { initializeTokenEventHandling } from '@/services/tokenEventHandler'
import { CurrentUserProvider } from '@/contexts/CurrentUserContext'
import { useHomeLiveRealtime } from '@/hooks/useHomeLiveRealtime'
import { useGlobalShortcut } from '@/hooks/useGlobalShortcut'
import { useIslandNavigation } from '@/hooks/useIslandNavigation'
import { LAYER } from '@/theme/layers'

function AppLayout() {
  const navigation = useIslandNavigation()
  const navigationId = useId()
  const skipRef = useRef<HTMLAnchorElement>(null)
  const mainRef = useRef<HTMLElement>(null)
  const isAuthenticated = useIsAuthenticated()
  const modal = navigation.open && !navigation.isDesktop

  // Layout effects release inert before the shared focus trap restores the
  // trigger. The actual page scroller is locked separately from document.body.
  useLayoutEffect(() => {
    if (!modal) return
    const elements = [skipRef.current, mainRef.current].filter(
      (element): element is HTMLElement => element !== null
    )
    const previous = elements.map((element) => element.hasAttribute('inert'))
    elements.forEach((element) => element.setAttribute('inert', ''))
    return () =>
      elements.forEach((element, index) =>
        element.toggleAttribute('inert', previous[index])
      )
  }, [modal])

  // Initialize the WebSocket compat manager only after authentication —
  // the socket auth callback requires getActiveAccount() to be non-null,
  // which is only guaranteed once MSAL has completed the auth flow. Token
  // event handling shares that same socket, so this is its one owner too:
  // starting/stopping it here means nothing connects before sign-in.
  useEffect(() => {
    if (!isAuthenticated) return
    websocketManager.initialize().catch(() => {
      // Connection failure is logged inside initialize(); no-op here.
    })
    const cleanupTokenEvents = initializeTokenEventHandling()
    return () => {
      cleanupTokenEvents()
      websocketManager.disconnect()
    }
  }, [isAuthenticated])

  // Home's live counts and Ask latency targets share these authenticated ticks.
  useHomeLiveRealtime()

  // Cmd/Ctrl+K from any page: bound once, at the only mount every route shares.
  useGlobalShortcut()

  return (
    <>
      {/* Outside RequireAuth: the sign-in interstitial replaces the shell's
          children, and a title unmounting with them would blank the tab. */}
      <DocumentTitle />
      <RequireAuth>
        <CurrentUserProvider>
          <div
            className="min-h-screen bg-ui-canvas text-ui-text pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]"
            data-testid="app-layout"
          >
            <div
              className="relative z-10 flex flex-col h-screen supports-[height:100dvh]:h-dvh overflow-hidden"
              data-testid="main-layout-container"
            >
              {/* Highest role still strictly under LAYER.modal: focus reveals
                  it over the rail and its scrim, never over a modal. */}
              <a
                ref={skipRef}
                href="#main-content"
                className={`app-skip-navigation ${LAYER.overlay}`}
              >
                Skip to content
              </a>
              <div
                className="app-navigation-body"
                data-navigation-pinned={navigation.pinned}
                data-testid="content-area"
              >
                <IslandSidebar
                  open={navigation.open}
                  pinned={navigation.pinned}
                  isDesktop={navigation.isDesktop}
                  navigationId={navigationId}
                  onToggle={navigation.toggle}
                  onExpand={navigation.expand}
                  onClose={navigation.close}
                  onPin={navigation.pin}
                  onSelect={navigation.select}
                />
                <main
                  ref={mainRef}
                  id="main-content"
                  className="app-navigation-main pb-[env(safe-area-inset-bottom)]"
                  data-navigation-modal={modal}
                  aria-label="Main content"
                  data-testid="main-content"
                  tabIndex={-1}
                >
                  <div
                    className="container mx-auto px-4 sm:px-6 py-8"
                    data-testid="content-container"
                  >
                    <Outlet />
                  </div>
                </main>
              </div>
            </div>
          </div>
        </CurrentUserProvider>
      </RequireAuth>
    </>
  )
}

export default memo(AppLayout)
